import { inject, Injectable } from '@angular/core';
import { ApiService } from './api.service';
import {
  EventsIDS,
  EventsIDSDisabled,
  Filter,
  GetEventsCenterMapViewResponse,
  GetEventsCountResponse,
  GetEventsFilterParams,
  GetEventsParams,
  GetEventsResponse,
  GetLastEventsResponse,
} from '@data';
import {
  ERROR_CONTEXT_EVENTS,
  ERROR_CONTEXT_HEADER,
  GET_EVENTS_CENTER_MAP_VIEW_ENDPOINT,
  GET_EVENTS_COUNT_ENDPOINT,
  GET_EVENTS_DISABLED_ENDPOINT,
  GET_EVENTS_ENDPOINT,
  GET_EVENTS_LAST_ENDPOINT,
  GET_EVENTS_MAP_VIEW_ENDPOINT,
} from '../constants/endpoints.constants';
import { HttpParams } from '@angular/common/http';
import { withSpinner } from '../../app/core/services/spinner.controller';

@Injectable({ providedIn: 'root' })
export class DataEventsService {
  protected readonly apiService = inject(ApiService);

  /**
   * Retrieves event markers for the map view using the /events/mapView endpoint.
   *
   * @param params - The filter and query parameters for the request.
   * @returns An Observable with the array of event markers for the map.
   */
  getEventsMapView(params: GetEventsFilterParams) {
    const { dateFrom, dateTo, unread, filters, centerId, query } = params;
    const queryObj: Record<
      string,
      string | number | boolean | readonly (string | number | boolean)[]
    > = {};
    if (dateFrom !== null && dateFrom !== undefined)
      queryObj['dateFrom'] = dateFrom;
    if (dateTo !== null && dateTo !== undefined) queryObj['dateTo'] = dateTo;
    if (unread !== null && unread !== undefined) queryObj['unread'] = unread;
    if (centerId) queryObj['centerId'] = centerId;
    // Puedes agregar aquí otros parámetros como distributorIds si es necesario
    const queryParams = new HttpParams().appendAll(queryObj);
    const body: any = this.getFiltersBody(query, filters);
    // El endpoint espera POST
    return this.apiService
      .post<any[]>(GET_EVENTS_MAP_VIEW_ENDPOINT, body, {
        params: queryParams,
      })
      .pipe(withSpinner());
  }

  getEventsCount(params?: GetEventsParams) {
    const queryParams = new HttpParams().appendAll({ ...params });
    return this.apiService
      .get<GetEventsCountResponse>(GET_EVENTS_COUNT_ENDPOINT, queryParams)
      .pipe(withSpinner());
  }

  getLastEvents(params?: GetEventsParams) {
    const queryParams = new HttpParams().appendAll({ ...params });
    return this.apiService
      .get<GetLastEventsResponse>(GET_EVENTS_LAST_ENDPOINT, queryParams)
      .pipe(withSpinner());
  }

  getEvents(params: GetEventsFilterParams) {
    const {
      dateFrom,
      dateTo,
      unread,
      filters,
      centerId,
      query,
      pageIndex,
      pageSize,
    } = params;
    const queryObj: Record<
      string,
      string | number | boolean | readonly (string | number | boolean)[]
    > = {};
    if (dateFrom !== null && dateFrom !== undefined)
      queryObj['dateFrom'] = dateFrom;
    if (dateTo !== null && dateTo !== undefined) queryObj['dateTo'] = dateTo;
    if (unread !== null && unread !== undefined) queryObj['unread'] = unread;
    if (centerId) queryObj['centerId'] = centerId;
    if (pageSize !== null && pageSize !== undefined)
      queryObj['pageSize'] = pageSize;
    if (pageIndex !== null && pageIndex !== undefined)
      queryObj['pageIndex'] = pageIndex;
    const queryParams = new HttpParams().appendAll(queryObj);
    const body: any = this.getFiltersBody(query, filters);
    return this.apiService
      .post<GetEventsResponse>(GET_EVENTS_ENDPOINT, body, {
        params: queryParams,
      })
      .pipe(withSpinner());
  }

  getGroupsEvents(params: GetEventsFilterParams) {
    const { dateFrom, unread } = params;
    const queryObj: Record<
      string,
      string | number | boolean | readonly (string | number | boolean)[]
    > = {};
    if (dateFrom !== null && dateFrom !== undefined)
      queryObj['dateFrom'] = dateFrom;
    if (unread !== null && unread !== undefined) queryObj['unread'] = unread;
    const queryParams = new HttpParams().appendAll(queryObj);
    const body: any = this.getFiltersBody('', []);
    return this.apiService
      .post<GetEventsResponse>(GET_EVENTS_ENDPOINT, body, {
        params: queryParams,
      })
      .pipe(withSpinner());
  }

  disabledEvents(eventIds?: EventsIDS) {
    const queryObj: Record<
      string,
      string | number | boolean | readonly (string | number | boolean)[]
    > = {};
    const queryParams = new HttpParams().appendAll(queryObj);
    return this.apiService
      .post<EventsIDSDisabled>(GET_EVENTS_DISABLED_ENDPOINT, eventIds, {
        params: queryParams,
        headers: {
          [ERROR_CONTEXT_HEADER]: ERROR_CONTEXT_EVENTS,
        },
      })
      .pipe(withSpinner());
  }

  /**
   * Retrieves detailed event data for a specific center, including transformer and line structure.
   * This endpoint is called when clicking on a map marker to get center-specific event details.
   * Uses POST method with the same filter structure as the events table API.
   *
   * @param params - Parameters including centerId, and the same filter params as getEvents
   * @returns An Observable containing the center event structure with transformers, panels, and lines
   */
  getEventsCenterMapView(params: GetEventsFilterParams & { centerId: string }) {
    const { centerId, dateFrom, dateTo, unread, filters, query } = params;
    const queryObj: Record<
      string,
      string | number | boolean | readonly (string | number | boolean)[]
    > = {};

    if (dateFrom !== null && dateFrom !== undefined)
      queryObj['dateFrom'] = dateFrom;
    if (dateTo !== null && dateTo !== undefined) queryObj['dateTo'] = dateTo;
    if (unread !== null && unread !== undefined) queryObj['unread'] = unread;

    const queryParams = new HttpParams().appendAll(queryObj);

    // Use the same filter body structure as getEvents
    const body: any = this.getFiltersBody(query, filters);

    // Replace centerId placeholder in endpoint URL
    const endpoint = GET_EVENTS_CENTER_MAP_VIEW_ENDPOINT.replace(
      '{{centerId}}',
      centerId,
    );

    return this.apiService
      .post<GetEventsCenterMapViewResponse>(endpoint, body, {
        params: queryParams,
      })
      .pipe(withSpinner());
  }

  private getFiltersBody(
    query: string | undefined,
    filters: Filter[] | undefined,
  ) {
    if (filters !== null && filters !== undefined) {
      let bodyFilters = filters!.map((filter) => ({
        ...filter,
        operator: filter?.operator || 'equals',
      }));
      bodyFilters = [...bodyFilters];
      const body: any = { filters: bodyFilters, query };
      return body;
    }
  }
}
